import { type FC, type ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { DEFAULT_WINDOW_ID } from "../../components/scheduleChanges/consts";
import type { ScheduleChangesWindow, ScheduleChangesWindowId } from "../../components/scheduleChanges/types";
import { useLastVisitWindow } from "../../hooks/useLastVisitWindow";
import { useScheduleChangesFeed } from "../../hooks/useScheduleChangesFeed";
import { isHeadNurse } from "../../utils/utilFunctions";
import { useAuth } from "../AuthProvider";
import { type ScheduleChangesActiveWindow, ScheduleChangesContext, type ScheduleChangesContextValue } from "./ScheduleChangesContext";
import { feedStatusOf } from "./scheduleChangesGate";
import { useChangeFilterChoices } from "./useChangeFilterChoices";

/**
 * The shared "what changed since my last visit" state behind the side panel's Changes tab: the active window, the
 * schedule-changes feed over it (fetched once here, and only while a surface that shows it is up —
 * `useScheduleChangesFeedConsumer`: the open panel, the bell's unseen dot), and the scheduler's filters — whose changes
 * and what kinds of change the tab lists, kept for the session.
 *
 * Mounted for every logged-in route (under `HospitalDataProvider`), but it only does anything for an admin with a
 * department loaded. For staff the value is inert — `isEnabled: false`, no request, nothing stored — so a surface both
 * audiences share can call `useScheduleChanges()` without a guard.
 */
export const ScheduleChangesProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const { userInfo } = useAuth();
	const isAdmin = isHeadNurse(userInfo.role);
	const lastVisit = useLastVisitWindow(isAdmin);
	const [activeId, setActiveId] = useState<ScheduleChangesWindowId>(DEFAULT_WINDOW_ID);
	const choices = useChangeFilterChoices();

	const requestedSince = useMemo(() => {
		if (!isAdmin || lastVisit === null) return null;
		const active = lastVisit.presets.find((preset) => preset.id === activeId);
		if (active === undefined) throw new Error(`schedule changes: no preset for window "${activeId}"`);
		return active.since;
	}, [isAdmin, lastVisit, activeId]);

	// The surfaces showing the changes right now (`useScheduleChangesFeedConsumer`): the feed is only asked for, and
	// polled, while there is one — not on every admin page.
	const feedConsumerCount = useRef(0);
	const [hasFeedConsumer, setHasFeedConsumer] = useState(false);
	const registerFeedConsumer = useCallback(() => {
		feedConsumerCount.current += 1;
		setHasFeedConsumer(true);
		return () => {
			feedConsumerCount.current -= 1;
			if (feedConsumerCount.current === 0) setHasFeedConsumer(false);
		};
	}, []);

	// Open-ended: the window runs up to "now", which the hook stamps on each request.
	const queryWindow = useMemo<ScheduleChangesWindow | null>(() => (requestedSince === null ? null : { since: requestedSince, until: null }), [requestedSince]);
	const query = useScheduleChangesFeed(queryWindow, hasFeedConsumer);
	const { refetch: refetchQuery } = query;
	const refetch = useCallback(() => {
		void refetchQuery();
	}, [refetchQuery]);
	const dataSince = query.data?.since;

	const changesWindow = useMemo<ScheduleChangesActiveWindow | null>(() => {
		if (requestedSince === null || lastVisit === null) return null;
		// The data starts where the request did — later than the preset once the window outgrew the server's cap.
		const since = dataSince !== undefined && dataSince > requestedSince ? dataSince : requestedSince;
		return { activeId, since, requestedSince, lastVisit: lastVisit.lastVisit, presets: lastVisit.presets, setActiveId };
	}, [requestedSince, lastVisit, activeId, dataSince]);

	const status = feedStatusOf({ isActive: changesWindow !== null, hasData: query.data !== undefined, isError: query.isError });
	// DEFAULT: no data yet (inert, loading, or failed) is no feed — `feed.status` says which, so a surface can tell "no
	// changes" from "not known".
	const data = status === "ready" && query.data !== undefined ? query.data.feed : null;

	const value = useMemo<ScheduleChangesContextValue>(
		() => ({
			isEnabled: changesWindow !== null,
			changesWindow,
			feed: { data, status, isLoading: status === "loading", isError: query.isError, error: query.error, refetch },
			whoFilter: choices.who,
			setWhoFilter: choices.setWho,
			actionFilter: choices.actions,
			setActionFilter: choices.setActions,
			registerFeedConsumer,
		}),
		[changesWindow, data, status, query.isError, query.error, refetch, choices.who, choices.setWho, choices.actions, choices.setActions, registerFeedConsumer],
	);

	return <ScheduleChangesContext.Provider value={value}>{children}</ScheduleChangesContext.Provider>;
};
